import type { ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  Activity,
  CalendarPlus,
  CalendarOff,
  Coffee,
  LogIn,
  LogOut,
  PlayCircle,
  CheckCircle2,
  XCircle,
  Trash2,
  Loader2,
} from 'lucide-react';
import { useActivityFeed, type ActivityFeedItem } from '../../hooks/useActivityFeed';

interface ActivityLogPanelProps {
  className?: string;
  /** Hide the built-in "Activity Log" header (the full-screen modal has its own). */
  hideHeader?: boolean;
}

interface EventStyle {
  Icon: ComponentType<{ className?: string }>;
  tint: string;
}

const DEFAULT_STYLE: EventStyle = { Icon: Activity, tint: 'bg-gray-50 text-gray-500' };

const EVENT_STYLES: Record<string, EventStyle> = {
  booking_scheduled: { Icon: CalendarPlus, tint: 'bg-blue-50 text-blue-600' },
  booking_unscheduled: { Icon: CalendarOff, tint: 'bg-amber-50 text-amber-600' },
  booking_in_progress: { Icon: PlayCircle, tint: 'bg-indigo-50 text-indigo-600' },
  booking_completed: { Icon: CheckCircle2, tint: 'bg-emerald-50 text-emerald-600' },
  booking_cancelled: { Icon: XCircle, tint: 'bg-red-50 text-red-600' },
  booking_deleted: { Icon: Trash2, tint: 'bg-gray-100 text-gray-500' },
  resource_clock_in: { Icon: LogIn, tint: 'bg-emerald-50 text-emerald-600' },
  resource_clock_out: { Icon: LogOut, tint: 'bg-gray-100 text-gray-600' },
  resource_break_start: { Icon: Coffee, tint: 'bg-amber-50 text-amber-600' },
  resource_break_end: { Icon: Coffee, tint: 'bg-sky-50 text-sky-600' },
};

function formatRelative(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return formatDistanceToNow(d, { addSuffix: true });
}

/** Control-centre live activity log — backend-driven feed (bookings, …). */
export default function ActivityLogPanel({ className, hideHeader = false }: ActivityLogPanelProps) {
  const navigate = useNavigate();
  const { items, loading, error } = useActivityFeed();

  const handleOpen = (item: ActivityFeedItem) => {
    if (item.entity_type === 'booking' && item.entity_id) {
      // Bookings page opens the booking from router state (see its navigation effect).
      navigate('/bookings', { state: { selectedBookingId: item.entity_id } });
    }
  };

  return (
    <section
      className={`relative z-0 overflow-hidden flex flex-col h-full ${className ?? ''}`}
      data-control-centre-panel
      aria-label="Activity log"
    >
      {!hideHeader ? (
        <header className="flex items-center gap-3 p-3 sm:p-4 min-w-0 shrink-0 border-b border-gray-100">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-50 border border-gray-100 text-gray-800"
            aria-hidden
          >
            <Activity className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-gray-900 truncate">Activity Log</h2>
            <p className="text-xs text-gray-500 mt-0.5 truncate">Live business activity</p>
          </div>
        </header>
      ) : null}

      <div className="control-centre-panel__body flex-1 min-h-0 overflow-y-auto ios-momentum-scroll">
        {loading ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-gray-300" />
          </div>
        ) : error ? (
          <div className="flex h-full flex-col items-center justify-center text-center px-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center px-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-50 border border-gray-100 text-gray-400 mb-3">
              <Activity className="h-5 w-5" />
            </div>
            <p className="text-sm font-medium text-gray-800">No activity yet</p>
            <p className="text-xs text-gray-500 mt-1 max-w-[220px]">
              Bookings, updates, and actions will appear here as they happen.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-50">
            {items.map((item) => {
              const style = EVENT_STYLES[item.event_type] ?? DEFAULT_STYLE;
              const Icon = style.Icon;
              const clickable = item.entity_type === 'booking' && !!item.entity_id;
              const meta = [item.actor_name, formatRelative(item.created_at)]
                .filter(Boolean)
                .join(' · ');
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => handleOpen(item)}
                    disabled={!clickable}
                    className={`flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors sm:px-4 ${
                      clickable ? 'hover:bg-gray-50 active:bg-gray-100 cursor-pointer' : 'cursor-default'
                    }`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <span
                      className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${style.tint}`}
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-semibold text-gray-900">
                        {item.title}
                      </span>
                      {item.description ? (
                        <span className="block truncate text-[12px] text-gray-600">
                          {item.description}
                        </span>
                      ) : null}
                      {meta ? (
                        <span className="mt-0.5 block truncate text-[11px] text-gray-400">{meta}</span>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
